import { useEffect, useRef, useState } from 'react';
import { Outlet, useLocation } from 'react-router-dom';

import { BthLogo } from '../brand/BthLogo';

import { NotificationsBell } from '../notifications/NotificationsBell';
import { CrmPageToastPortal } from '../notifications/CrmPageToastPortal';
import { NotificationPermissionPrompt } from '../notifications/NotificationPermissionPrompt';
import { getNotificationPermission } from '../../lib/browserNotifications';
import { shouldShowInAppToastForCrmAlert } from '../../lib/crmNotificationPresentation';
import { useNotificationAlerts } from '../../hooks/useNotificationAlerts';
import { useTicketUnreadBadge } from '../../hooks/useTicketUnreadBadge';

import { GlobalSearch } from '../search/GlobalSearch';
import { ItFloatingShell } from './ItFloatingShell';
import { MobileBottomNav } from './MobileBottomNav';
import { PortalSwitcher } from './PortalSwitcher';

import { DataStatusBanner } from './DataStatusBanner';
import { PortalPresenceReporter } from './PortalPresenceReporter';

import { useAuth } from '../../contexts/AuthContext';
import { useCrmPageNotifications } from '../../contexts/CrmPageNotificationsContext';
import { usePortal, usePortalPath } from '../../contexts/PortalContext';
import { useCrmData } from '../../contexts/CrmDataContext';
import { canShowFleetNav } from '../../lib/fleetPermissions';
import { formatDisplayPersonName } from '../../lib/formatPersonName';
import { isSoftPortalEntryPending } from '../../lib/portal';
import { recoverMobileViewportAfterPortalEntry } from '../../lib/mobileViewport';
import type { CrmNotification, UserRole } from '../../types/crm';
import '../../styles/dem-theme-lab.css';

const VIEW_ROLES: UserRole[] = ['admin', 'user'];

export function AppShell() {
  const { kind } = usePortal();
  const home = usePortalPath();
  const accountsPath = usePortalPath('accounts');
  const contactsPath = usePortalPath('contacts');
  const projectsPath = usePortalPath('projects');
  const tasksPath = usePortalPath('tasks');
  const analyticsPath = usePortalPath('analytics');
  const fleetPath = usePortalPath('fleet');
  const cataloguePath = usePortalPath('catalogue');
  const dataGovSgPath = usePortalPath('data-gov-sg');
  const ticketsPath = usePortalPath('tickets');
  const approvalsPath = usePortalPath('approvals');
  const ticketUnread = useTicketUnreadBadge();
  const { pathname } = useLocation();
  const prevTicketUnread = useRef<number | null>(null);

  const { profile, signOut } = useAuth();

  // choose-portal → CRM/IT: reset iOS Web App viewport once the dock exists.
  useEffect(() => {
    if (!isSoftPortalEntryPending()) return;
    recoverMobileViewportAfterPortalEntry();
  }, []);

  // IT floating chrome: no bottom dock / topbar clearance.
  useEffect(() => {
    if (kind !== 'it') return;
    const root = document.documentElement;
    root.classList.remove('has-dock');
    root.style.removeProperty('--dock-clearance');
    root.style.removeProperty('--mobile-header-clearance');
    root.style.setProperty('--dock-clearance', '0px');
    root.style.setProperty('--mobile-header-clearance', '0px');
    return () => {
      root.style.removeProperty('--dock-clearance');
      root.style.removeProperty('--mobile-header-clearance');
    };
  }, [kind]);

  const {
    viewRole,
    realRole,
    setViewRole,
    userFilter,
    setUserFilter,
    canUseUserFilter,
    canUseViewAs,
    canApproveTransfers,
    ownerOptions,
    transferRequests,
    username,
    departmentSlug,
  } = useCrmData();

  const actorRole = canUseViewAs ? viewRole : realRole;
  const showFleet = kind !== 'it' && canShowFleetNav(actorRole, departmentSlug);
  const isItPortal = kind === 'it';

  const nav = [
    { to: home, label: 'Dashboard', icon: '▣' },
    { to: accountsPath, label: 'Accounts', icon: '◎' },
    { to: contactsPath, label: 'Contacts', icon: '◉' },
    { to: projectsPath, label: 'Projects', icon: '◇' },
    { to: tasksPath, label: 'Tasks', icon: '☑' },
    ...(!isItPortal ? [{ to: analyticsPath, label: 'Analytics', icon: '▥' }] : []),
    ...(showFleet ? [{ to: fleetPath, label: 'Fleet', icon: '🚛' }] : []),
    { to: ticketsPath, label: 'Tickets', icon: '✉', badge: ticketUnread },
    ...(!isItPortal
      ? [
          { to: cataloguePath, label: 'Catalogue', icon: '▤' },
          { to: dataGovSgPath, label: 'Open Data', icon: '🌐' },
        ]
      : []),
  ];

  const accessBadge = (() => {
    const dept = (departmentSlug ?? '').trim().toLowerCase();
    if (dept === 'it' || dept.startsWith('it_') || dept.includes('information')) return 'IT';
    if (dept.includes('management')) return 'MGMT';
    return realRole.toUpperCase();
  })();

  useEffect(() => {
    if (userFilter !== 'Me') return;
    setUserFilter(username || 'All');
  }, [userFilter, username, setUserFilter]);

  const {
    pendingToasts,
    clearPendingToasts,
    openAlert,
    setupState,
    showPermissionPrompt,
    enableNotifications,
    dismissPermissionPrompt,
    webPushConfigured,
  } = useNotificationAlerts();

  const { push, dismissToast } = useCrmPageNotifications();
  const pendingCrmAlertRef = useRef<CrmNotification | null>(null);
  const lastViewRoleToastRef = useRef<UserRole | null>(null);
  const [toastClickable, setToastClickable] = useState(false);
  const [mobileFiltersOpen, setMobileFiltersOpen] = useState(false);
  const mobileFiltersRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const prev = prevTicketUnread.current;
    prevTicketUnread.current = ticketUnread;
    if (prev == null || ticketUnread <= prev) return;
    const added = ticketUnread - prev;
    setToastClickable(false);
    pendingCrmAlertRef.current = null;
    push({
      tone: 'info',
      title: added === 1 ? 'New email' : `${added} new emails`,
      lines: pathname.startsWith(ticketsPath) ? ['Inbox has a new unread ticket.'] : ['Open Tickets to read.'],
    });
  }, [ticketUnread, pathname, ticketsPath, push]);

  useEffect(() => {
    if (pendingToasts.length === 0) return;
    const browserNotificationsActive = getNotificationPermission() === 'granted';
    const toastable = pendingToasts.filter((alert) =>
      shouldShowInAppToastForCrmAlert(alert, browserNotificationsActive),
    );

    if (toastable.length > 0) {
      pendingCrmAlertRef.current = toastable[0] ?? null;
      setToastClickable(Boolean(toastable[0]?.link || toastable[0]?.id));
      for (const alert of toastable) {
        push({
          tone: 'info',
          title: alert.title,
          lines: alert.body ? [alert.body] : [],
        });
      }
    } else {
      pendingCrmAlertRef.current = null;
      setToastClickable(false);
    }

    clearPendingToasts();
  }, [pendingToasts, clearPendingToasts, push]);

  useEffect(() => {
    if (!canUseViewAs) return;
    if (viewRole === realRole) return;
    if (mobileFiltersOpen) return;
    // Soft choose→portal: role/elevation settles a beat late on mobile — wait it out
    // so we never flash "Viewing as User" from a transient allowlist row.
    if (isSoftPortalEntryPending()) return;

    const settleMs = 450;
    const timer = window.setTimeout(() => {
      if (isSoftPortalEntryPending()) return;

      const key = `bth_view_as_toast_${viewRole}`;
      try {
        if (sessionStorage.getItem(key) === '1') return;
        sessionStorage.setItem(key, '1');
      } catch {
        if (lastViewRoleToastRef.current === viewRole) return;
        lastViewRoleToastRef.current = viewRole;
      }

      setToastClickable(false);
      pendingCrmAlertRef.current = null;
      const label = viewRole.charAt(0).toUpperCase() + viewRole.slice(1);
      push({
        tone: 'warn',
        title: `Viewing as ${label}`,
        lines: ['Create/edit and full lists are limited for this role.'],
      });
    }, settleMs);

    return () => window.clearTimeout(timer);
  }, [viewRole, realRole, canUseViewAs, push, mobileFiltersOpen]);

  const pendingTransferCount = transferRequests.filter((r) => r.status === 'pending').length;
  const welcomeName =
    formatDisplayPersonName(profile?.display_name?.trim() || profile?.username) || 'there';
  const showFilters = canUseViewAs || canUseUserFilter;
  const filterSummary = [
    canUseViewAs ? viewRole.charAt(0).toUpperCase() + viewRole.slice(1) : null,
    canUseUserFilter
      ? userFilter === 'All'
        ? 'All users'
        : ownerOptions.find((o) => o.username === userFilter)?.display_name || userFilter
      : null,
  ]
    .filter(Boolean)
    .join(' · ');

  useEffect(() => {
    if (!mobileFiltersOpen) return undefined;
    function onDoc(ev: MouseEvent) {
      if (mobileFiltersRef.current && !mobileFiltersRef.current.contains(ev.target as Node)) {
        setMobileFiltersOpen(false);
      }
    }
    function onKey(ev: KeyboardEvent) {
      if (ev.key === 'Escape') setMobileFiltersOpen(false);
    }
    document.addEventListener('mousedown', onDoc);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDoc);
      document.removeEventListener('keydown', onKey);
    };
  }, [mobileFiltersOpen]);

  function handleToastOpen() {
    if (pendingCrmAlertRef.current) {
      void openAlert(pendingCrmAlertRef.current);
      pendingCrmAlertRef.current = null;
    }
    setToastClickable(false);
    dismissToast();
  }

  const notificationsBell = (
    <NotificationsBell
      setupState={setupState}
      onEnableNotifications={() => void enableNotifications()}
      webPushConfigured={webPushConfigured}
    />
  );

  const notificationsBellAbove = (
    <NotificationsBell
      setupState={setupState}
      onEnableNotifications={() => void enableNotifications()}
      webPushConfigured={webPushConfigured}
      placement="above"
    />
  );

  const filterControlsDesktop = showFilters ? (
    <div className="topbar__filters topbar__filters--desktop dem-theme-lab__it-filters">
      {canUseViewAs && (
        <label className="topbar__filter">
          <span className="topbar__filter-label">View as</span>
          <select
            className="field-input field-input--compact"
            value={viewRole}
            onChange={(e) => setViewRole(e.target.value as UserRole)}
          >
            {VIEW_ROLES.map((r) => (
              <option key={r} value={r}>
                {r.charAt(0).toUpperCase() + r.slice(1)}
              </option>
            ))}
          </select>
        </label>
      )}
      {canUseUserFilter && (
        <label className="topbar__filter">
          <span className="topbar__filter-label">User</span>
          <select
            className="field-input field-input--compact"
            value={userFilter}
            onChange={(e) => setUserFilter(e.target.value)}
          >
            <option value="All">All</option>
            {ownerOptions.map((o) => (
              <option key={o.value} value={o.username}>
                {o.display_name}
              </option>
            ))}
          </select>
        </label>
      )}
    </div>
  ) : null;

  const filterControlsMobile = showFilters ? (
    <div className="topbar__filters-mobile dem-theme-lab__it-filters-mobile" ref={mobileFiltersRef}>
      <button
        type="button"
        className="topbar__filters-trigger"
        aria-expanded={mobileFiltersOpen}
        aria-haspopup="dialog"
        onClick={() => {
          setMobileFiltersOpen((v) => {
            const next = !v;
            if (next) dismissToast();
            return next;
          });
        }}
      >
        <span className="topbar__filters-trigger-label">Filters</span>
        <span className="topbar__filters-trigger-value">{filterSummary || 'View'}</span>
      </button>
      {mobileFiltersOpen && (
        <div className="topbar__filters-sheet" role="dialog" aria-label="View filters">
          {canUseViewAs && (
            <label className="topbar__filter topbar__filter--stack">
              <span className="topbar__filter-label">View as</span>
              <select
                className="field-input field-input--compact"
                value={viewRole}
                onChange={(e) => setViewRole(e.target.value as UserRole)}
              >
                {VIEW_ROLES.map((r) => (
                  <option key={r} value={r}>
                    {r.charAt(0).toUpperCase() + r.slice(1)}
                  </option>
                ))}
              </select>
            </label>
          )}
          {canUseUserFilter && (
            <label className="topbar__filter topbar__filter--stack">
              <span className="topbar__filter-label">User</span>
              <select
                className="field-input field-input--compact"
                value={userFilter}
                onChange={(e) => setUserFilter(e.target.value)}
              >
                <option value="All">All</option>
                {ownerOptions.map((o) => (
                  <option key={o.value} value={o.username}>
                    {o.display_name}
                  </option>
                ))}
              </select>
            </label>
          )}
          <button
            type="button"
            className="btn-primary btn-sm"
            onClick={() => setMobileFiltersOpen(false)}
          >
            Done
          </button>
        </div>
      )}
    </div>
  ) : null;

  const itNavItems = [
    ...nav.map((item) => ({ ...item, end: item.to === home })),
    ...(canApproveTransfers
      ? [
          {
            to: approvalsPath,
            label:
              pendingTransferCount > 0 ? `Approvals (${pendingTransferCount})` : 'Approvals',
            icon: '✓',
          },
        ]
      : []),
  ];

  if (isItPortal) {
    return (
      <div className="app-shell app-shell--dock app-shell--it app-shell--dem-theme-lab">
        <PortalPresenceReporter portal={kind} />
        <div className="main">
          {showPermissionPrompt && (
            <NotificationPermissionPrompt
              setupState={setupState}
              onEnable={() => void enableNotifications()}
              onDismiss={dismissPermissionPrompt}
              webPushConfigured={webPushConfigured}
            />
          )}
          <DataStatusBanner />
          <div className="page">
            <ItFloatingShell
              items={itNavItems}
              topExtra={
                <>
                  {filterControlsDesktop}
                  {filterControlsMobile}
                </>
              }
              notifications={notificationsBellAbove}
              mobileNotifications={notificationsBell}
            >
              <Outlet />
            </ItFloatingShell>
          </div>
        </div>
        <CrmPageToastPortal onOpen={toastClickable ? handleToastOpen : undefined} />
      </div>
    );
  }

  return (
    <div className="app-shell app-shell--dock">
      <PortalPresenceReporter portal={kind} />
      <div className="main">
        <header className="topbar">
          <div className="topbar__brand">
            <BthLogo className="topbar__logo" />
          </div>

          <p className="topbar__title">
            Welcome back, <span className="topbar__name">{welcomeName}</span>!
          </p>

          <div className="topbar__controls">
            <GlobalSearch />
            {filterControlsDesktop}

            <div
              className="topbar__meta"
              title={`${profile?.display_name ?? 'User'} · ${accessBadge}${viewRole !== realRole ? ` (view: ${viewRole})` : ''}`}
            >
              <span className="topbar__role">{accessBadge}</span>
            </div>

            <div className="topbar__actions">
              {filterControlsMobile}

              {notificationsBell}

              <PortalSwitcher current={kind} />

              <button
                type="button"
                className="btn-secondary btn-sm topbar__signout"
                aria-label="Sign out"
                onClick={() => void signOut()}
              >
                <span className="topbar__signout-icon" aria-hidden>
                  ⎋
                </span>
                <span className="topbar__signout-label">Sign out</span>
              </button>
            </div>
          </div>
        </header>

        {showPermissionPrompt && (
          <NotificationPermissionPrompt
            setupState={setupState}
            onEnable={() => void enableNotifications()}
            onDismiss={dismissPermissionPrompt}
            webPushConfigured={webPushConfigured}
          />
        )}

        <DataStatusBanner />

        <div className="page">
          <Outlet />
        </div>
      </div>

      <MobileBottomNav
        items={nav.map((item) => ({ ...item, end: item.to === home }))}
        extraItems={[
          ...(canApproveTransfers
            ? [
                {
                  to: approvalsPath,
                  label:
                    pendingTransferCount > 0 ? `Approvals (${pendingTransferCount})` : 'Approvals',
                  icon: '✓',
                },
              ]
            : []),
        ]}
      />

      <CrmPageToastPortal onOpen={toastClickable ? handleToastOpen : undefined} />
    </div>
  );
}
